import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setPublisherList, setPublisherMap } from '../redux/actions';
import { api } from '../services/api';

const noOp = () => {};

const useOutsideClick = (node, cb, onMount = noOp, deps = []) => {
  useEffect(() => {
    onMount();

    const onWindowClick = e => {
      if (!node.current) return;

      if (!node.current.contains(e.target)) {
        cb(false);
      }
    };

    window.addEventListener('click', onWindowClick);
    return () => {
      window.removeEventListener('click', onWindowClick);
    };
  }, [...deps]);
};

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value]);

  return debouncedValue;
}

const mapPublishers = pubs => {
  return pubs.map(pub => {
    return { label: pub.full_name, name: pub.pub_id, selected: true };
  });
};

const mapPublishersV2 = pubs => {
  return pubs.map(pub => ({ label: pub.full_name, value: pub.pub_id }));
};

const getPublishersMap = pubs => {
  const pubMap = {};
  pubs.forEach(pub => {
    pubMap[pub.pub_id] = pub.full_name;
  });

  return pubMap;
};

function usePublisherList(orgId, dropdown = false, v2 = false) {
  const publishers = useSelector(state => state.publisherList) || [];
  const publishersMap = useSelector(state => state.publisherMap) || {};
  const [pubOptions, setPubOptions] = useState(
    publishers.length ? (v2 ? mapPublishersV2(publishers) : mapPublishers(publishers)) : [],
  );
  const [pubMap, setPubMap] = useState(publishersMap);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchPubList = async () => {
      const pubListEndpoint = `/api/v0/merchants/${orgId}/strategy/type_ahead/`;
      const resp = await api.get(pubListEndpoint, { type: 'publisher' });

      const pubList = resp.data.data[0].stats.sort((pub1, pub2) => {
        return pub1.full_name.toLowerCase() > pub2.full_name.toLowerCase() ? 1 : -1;
      });
      const pubMap = getPublishersMap(pubList);
      dispatch(setPublisherList(pubList));
      dispatch(setPublisherMap(pubMap));

      setPubOptions(v2 ? mapPublishersV2(pubList) : mapPublishers(pubList));
      setPubMap(pubMap);
    };

    if (!publishers.length) {
      fetchPubList();
    }
  }, []);

  if (dropdown) {
    return [pubOptions, setPubOptions, pubMap];
  }
  return publishers;
}

const useForm = (onSubmit, validators) => {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  const handleSubmit = e => {
    if (e) e.preventDefault();

    const fieldErrors = {};
    let isInvalid = false;

    Object.entries(validators).forEach(([field, fieldValidators]) => {
      const fieldValue = values[field];
      fieldValidators.forEach(({ validatorFn, errorMessage }) => {
        if (!validatorFn(fieldValue, values)) {
          isInvalid = true;
          if (!fieldErrors[field]) {
            fieldErrors[field] = [errorMessage];
          } else {
            fieldErrors[field].push(errorMessage);
          }
        }
      });
    });

    if (isInvalid) {
      setErrors(fieldErrors);
    } else {
      setIsDirty(false);
      onSubmit(values);
    }
  };

  const handleChange = e => {
    e.persist && e.persist();
    setIsDirty(true);
    const newValues = { ...values, [e.target.name]: e.target.value };
    setValues(newValues);
    return newValues;
  };

  return {
    handleChange,
    handleSubmit,
    values,
    setValues,
    errors,
    setErrors,
    isDirty,
  };
};

export { useDebounce, useOutsideClick, usePublisherList, useForm };
